import { Connector } from '@web3-react/types';
export class NoPhantomError extends Error {
    constructor() {
        super('Phantom not installed');
        this.name = NoPhantomError.name;
        Object.setPrototypeOf(this, NoPhantomError.prototype);
    }
}
function parseChainId(chainId) {
    return Number.parseInt(chainId, 16);
}
export class Phantom extends Connector {
    constructor({ actions, options, onError }) {
        super(actions, onError);
        this.options = options;
    }
    async isomorphicInitialize() {
        if (this.eagerConnection)
            return;
        return (this.eagerConnection = import('./detect').then(async (m) => {
            const provider = await m.default({ mustBePhantom: false, ...this.options });
            if (provider.isPhantom) {
                this.provider = provider;
                this.provider.on('connect', ({ chainId }) => {
                    this.actions.update({ chainId: parseChainId(chainId) });
                });
                this.provider.on('disconnect', (error) => {
                    this.actions.resetState();
                    this.onError?.(error);
                });
                this.provider.on('chainChanged', (chainId) => {
                    this.actions.update({ chainId: parseChainId(chainId) });
                });
                this.provider.on('accountsChanged', (accounts) => {
                    if (accounts.length === 0) {
                        // handle this edge case by disconnecting
                        this.actions.resetState();
                    }
                    else {
                        this.actions.update({ accounts });
                    }
                });
            }
        }));
    }
    /** {@inheritdoc Connector.connectEagerly} */
    async connectEagerly() {
        const cancelActivation = this.actions.startActivation();
        await this.isomorphicInitialize();
        if (!this.provider)
            return cancelActivation();
        return Promise.all([
            this.provider.request({ method: 'eth_chainId' }),
            this.provider.request({ method: 'eth_accounts' }),
        ])
            .then(([chainId, accounts]) => {
            if (accounts.length) {
                this.actions.update({ chainId: parseChainId(chainId), accounts });
            }
            else {
                throw new Error('No accounts returned');
            }
        })
            .catch((error) => {
            console.debug('Could not connect eagerly', error);
            cancelActivation();
        });
    }
    /**
     * Initiates a connection.
     *
     * @param desiredChainIdOrChainParameters - If defined, indicates the desired chain to connect to. If the user is
     * already connected to this chain, no additional steps will be taken. Otherwise, the user will switch
     * to the chain.
     */
    async activate(desiredChainIdOrChainParameters) {
        let cancelActivation;
        if (!this.provider?.isConnected?.())
            cancelActivation = this.actions.startActivation();
        return this.isomorphicInitialize()
            .then(async () => {
            if (!this.provider)
                throw new NoPhantomError();
            return Promise.all([
                this.provider.request({ method: 'eth_chainId' }),
                this.provider.request({ method: 'eth_requestAccounts' }),
            ]).then(([chainId, accounts]) => {
                const receivedChainId = parseChainId(chainId);
                const desiredChainId = typeof desiredChainIdOrChainParameters === 'number'
                    ? desiredChainIdOrChainParameters
                    : desiredChainIdOrChainParameters?.chainId;
                // if there's no desired chain, or it's equal to the received, update
                if (!desiredChainId || receivedChainId === desiredChainId)
                    return this.actions.update({ chainId: receivedChainId, accounts });
                const desiredChainIdHex = `0x${desiredChainId.toString(16)}`;
                // if we're here, we can try to switch networks
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return this.provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: desiredChainIdHex }],
                })
                    .catch((error) => {
                    if (error.code === 4902 && typeof desiredChainIdOrChainParameters !== 'number') {
                        // if we're here, we can try to add a new network
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        return this.provider.request({
                            method: 'wallet_addEthereumChain',
                            params: [{ ...desiredChainIdOrChainParameters, chainId: desiredChainIdHex }],
                        });
                    }
                    throw error;
                })
                    .then(() => this.activate(desiredChainId));
            });
        })
            .catch((error) => {
            cancelActivation?.();
            throw error;
        });
    }
}
